import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';
import { auth } from './firebase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,

      setTokens: (accessToken, refreshToken) =>
        set({ accessToken, refreshToken }),

      /**
       * Called after Firebase Google sign-in.
       * Sends the Firebase ID token to eonik-backend POST /api/auth/google.
       * Backend verifies it via Firebase Admin, creates/finds the user in Postgres,
       * and returns its own { access_token, refresh_token, user } JWT pair.
       */
      login: async (firebaseUser) => {
        const idToken = await firebaseUser.getIdToken();
        const { data } = await axios.post(
          `${BACKEND_URL}/api/auth/google`,
          { id_token: idToken }
        );
        set({
          user: data.user,
          accessToken: data.access_token,
          refreshToken: data.refresh_token,
          isAuthenticated: true,
        });
        return data.user;
      },

      /**
       * Re-fetches fresh user data (credits, etc.) from the backend.
       */
      refreshUser: async () => {
        try {
          const { data } = await axios.get(`${BACKEND_URL}/api/auth/me`);
          set({ user: data, isAuthenticated: true });
          return data;
        } catch {
          return get().user;
        }
      },

      logout: async () => {
        try { await auth.signOut(); } catch {}
        set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false });
      },
    }),
    {
      name: 'ab-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// ── Request interceptor: attach JWT access token ──────────────────────────
axios.interceptors.request.use((config) => {
  const { accessToken } = useAuthStore.getState();
  if (accessToken) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// ── Response interceptor: auto-refresh JWT on 401 ────────────────────────
let _isRefreshing = false;
let _refreshQueue = [];

const _drainQueue = (newToken, error) => {
  _refreshQueue.forEach(({ resolve, reject }) =>
    error ? reject(error) : resolve(newToken)
  );
  _refreshQueue = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    if (error.response?.status !== 401 || original._retry) {
      return Promise.reject(error);
    }

    const { refreshToken, logout, setTokens } = useAuthStore.getState();
    if (!refreshToken) {
      logout();
      return Promise.reject(error);
    }

    if (_isRefreshing) {
      return new Promise((resolve, reject) => {
        _refreshQueue.push({ resolve, reject });
      }).then((token) => {
        original.headers.Authorization = `Bearer ${token}`;
        return axios(original);
      });
    }

    original._retry = true;
    _isRefreshing = true;

    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/auth/refresh`,
        { refresh_token: refreshToken },
        { headers: { Authorization: undefined } } // don't attach expired token to refresh call
      );
      setTokens(data.access_token, data.refresh_token);
      _drainQueue(data.access_token, null);
      original.headers.Authorization = `Bearer ${data.access_token}`;
      return axios(original);
    } catch (refreshError) {
      _drainQueue(null, refreshError);
      logout();
      return Promise.reject(refreshError);
    } finally {
      _isRefreshing = false;
    }
  }
);
